import React, { useState, useEffect, useCallback } from "react";
import * as FaIcons from "react-icons/fa";
import i18n from "i18n-react";
import { Row, Col, Table, Input, FormGroup, Label, Alert } from 'reactstrap';

import { DefaultAjax } from "../../components/DefaultAjax";
import DefaultPagination from "../../components/DefaultPagination";
import {
    catchErrorAjax,
    calculateScroll,
    handleIndexDefaultSearchButton,
    toastFunction
} from "../../components/mainFunctions";
import DefaultSpinner from "../../components/DefaultSpinner";
import DefaultModal from "../../components/DefaultModal";
import PropTypes from 'prop-types';

export const url = process.env.REACT_APP_API_URL;

const InvoiceNumberInvalidate = ({ token }) => {
    const [arrayInvoices, setArrayInvoices] = useState([]);
    const [spinner, setSpinner] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState(null);

    const [filterMonth, setFilterMonth] = useState("");
    const [filterYear, setFilterYear] = useState("");
    const [filterStatus, setFilterStatus] = useState("PENDIENTE");

    // Modal State
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [invalidationMotive, setInvalidationMotive] = useState("");
    const [motiveError, setMotiveError] = useState("");

    const perPage = 10;

    const years = Array.from({ length: 6 }, (_, i) => 2026 - i);
    const months = [
        { val: "01", name: "Enero" }, { val: "02", name: "Febrero" }, { val: "03", name: "Marzo" },
        { val: "04", name: "Abril" }, { val: "05", name: "Mayo" }, { val: "06", name: "Junio" },
        { val: "07", name: "Julio" }, { val: "08", name: "Agosto" }, { val: "09", name: "Septiembre" },
        { val: "10", name: "Octubre" }, { val: "11", name: "Noviembre" }, { val: "12", name: "Diciembre" }
    ];

    const getInvoices = useCallback((isSearch = false) => {
        setSpinner(<DefaultSpinner />);

        const pageToFetch = isSearch ? 1 : currentPage;

        if (isSearch && currentPage !== 1) {
            setCurrentPage(1);
            return;
        }

        const params = new URLSearchParams();
        params.append("max", perPage);
        params.append("pageNumber", pageToFetch - 1); // Backend waits 0-indexed

        if (filterMonth) params.append("month", filterMonth);
        if (filterYear) params.append("year", filterYear);

        if (filterStatus) params.append("status", filterStatus);

        DefaultAjax('').get(`/fePyInvoiceNumberInvalidate?${params.toString()}`)
            .then((res) => {
                if (res?.data) {
                    const result = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;

                    // Estructura Page de Spring Boot
                    setArrayInvoices(result.content || []);
                    setTotalCount(result.totalElements || 0);
                }
                setSpinner("");
            })
            .catch((error) => {
                setSpinner("");
                catchErrorAjax(error, (errState) => {
                    if (errState.spinner !== undefined) setSpinner(errState.spinner);
                });
            });
    }, [currentPage, filterMonth, filterYear, filterStatus]);


    useEffect(() => {
        const bodyElement = document.getElementById('bodyContent');
        if (bodyElement) calculateScroll(80);
        getInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    useEffect(() => {
        const handler = (e) => handleIndexDefaultSearchButton(e, () => getInvoices(true));
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [getInvoices]);

    const handlePageClick = (e) => {
        setCurrentPage(e.selected + 1);
        setExpandedRow(null);
    };

    const renderStatusBadge = (status) => {
        const s = status?.toLowerCase();
        let color = "bg-status-pending";
        if (s === 'enviado' || s === 'aprobado') color = "bg-status-sent";
        if (s === 'expirado' || s === 'rechazado') color = "bg-status-expired";
        return <div className={`status-badge-oval ${color} text-center`}>{status?.toUpperCase()}</div>;
    };

    const toggleModal = () => setModalOpen(!modalOpen);

    const handleOpenModal = (id) => {
        setSelectedInvoiceId(id);
        setInvalidationMotive("");
        setMotiveError("");
        setModalOpen(true);
    };

    const handleConfirmInvalidate = () => {
        if (invalidationMotive.length < 5 || invalidationMotive.length > 500) {
            setMotiveError("El motivo debe tener entre 5 y 500 caracteres.");
            return;
        }

        setSpinner(<DefaultSpinner />);
        DefaultAjax('').post(`/fePyInvoiceNumberInvalidate/invalidateDocument`, {
            invalidateId: selectedInvoiceId,
            motive: invalidationMotive
        })
            .then((res) => {
                setSpinner("");
                setModalOpen(false);
                getInvoices();
                toastFunction(i18n.translate("send_invalidate"), "success");
            })
            .catch((error) => {
                setSpinner("");
                catchErrorAjax(error, (errState) => {
                    if (errState.spinner !== undefined) setSpinner(errState.spinner);
                });
            });
    };


    const getInvoiceDate = (inv) => {
        if (inv?.dateAcceptInvalidation) {
            return new Date(inv.dateAcceptInvalidation).toLocaleDateString();
        }
        if (inv?.dateRequestInvalidation) {
            return new Date(inv.dateRequestInvalidation).toLocaleDateString();
        }
        return "-";
    };

    return (
        <div className="container-fluid">
            <nav className='navbar'>
                <Row style={{ width: '100%' }}>
                    <Col md="2">
                        <label className="label-standard">{i18n.translate("month")}</label>
                        <select className="form-control selectStandard" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
                            <option value="">{i18n.translate("default_all")}</option>
                            {months.map(m => <option key={m.val} value={m.val}>{m.name}</option>)}
                        </select>
                    </Col>
                    <Col md="2">
                        <label className="label-standard">{i18n.translate("year")}</label>
                        <select className="form-control selectStandard" value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                            <option value="">{i18n.translate("default_all")}</option>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </Col>
                    <Col md="2">
                        <label className="label-standard">{i18n.translate("status")}</label>
                        <select id="statusFilter" className="form-control selectStandard" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                            <option value="">{i18n.translate("default_all")}</option>
                            <option value="PENDIENTE">{i18n.translate("pending")}</option>
                            <option value="APROBADO">{i18n.translate("approved")}</option>
                            <option value="RECHAZADO">{i18n.translate("rejected")}</option>
                            <option value="EXPIRADO">{i18n.translate("expired")}</option>
                        </select>
                    </Col>

                    <Col style={{ paddingTop: "22px" }}>
                        <button className='buttonMzateSecondary' onClick={() => getInvoices(true)}>
                            <FaIcons.FaSearch /> <span>{i18n.translate("default_search")}</span>
                        </button>
                    </Col>
                </Row>
            </nav>

            <div id='bodyContent' className="bg-white p-3 shadow-sm rounded">
                {spinner}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="title">{i18n.translate("invoice_number_invalidate")}</h5>
                    <div className="pagination-top">
                        {/* Pasamos data={arrayInvoices} para evitar el error de length en el hijo */}
                        <DefaultPagination
                            data={arrayInvoices}
                            handlePageClick={handlePageClick}
                            perPage={perPage}
                            totalCount={totalCount}
                            forcePage={currentPage - 1}
                        />
                    </div>
                </div>

                <Table responsive hover className="table-outline">
                    <thead className="thead-light">
                        <tr>
                            <th style={{ width: '50px' }}></th>
                            <th className="text-center">{i18n.translate("state")}</th>
                            <th className="text-center">{i18n.translate("date")}</th>
                            <th className="text-center">{i18n.translate("solicitant")}</th>
                            <th className="text-center">{i18n.translate("actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {arrayInvoices && arrayInvoices.length > 0 ? (
                            arrayInvoices.map((inv) => (
                                <React.Fragment key={inv.id}>
                                    <tr onClick={() => setExpandedRow(expandedRow === inv.id ? null : inv.id)} style={{ cursor: 'pointer' }}>
                                        <td>{expandedRow === inv.id ? <FaIcons.FaChevronUp /> : <FaIcons.FaChevronDown />}</td>
                                        <td className="text-center">{renderStatusBadge(inv.state || inv.status)}</td>
                                        <td className="text-center">
                                            {getInvoiceDate(inv)}
                                        </td>
                                        <td className="text-center">{inv.applicantFullname || inv.requestedBy || ""}</td>
                                        <td className="text-center">
                                            {(inv.state || inv.status)?.toUpperCase() === 'PENDIENTE' && (
                                                <button className="btn btn-sm btn-success rounded-pill px-3" onClick={(e) => { e.stopPropagation(); handleOpenModal(inv.id); }}>
                                                    {i18n.translate("invalidate")}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {expandedRow === inv.id && (
                                        <tr className="bg-light">
                                            <td colSpan="5" className="p-4">
                                                <div className="border p-4 bg-white shadow-sm rounded">
                                                    <div className="row">
                                                        {/* COLUMNA DERECHA: RANGOS (DETAILS) */}
                                                        <div className="col-md-7 ps-md-4">
                                                            <h6 className="text-primary mb-3">{i18n.translate("range_of_numbering_invalidated")}</h6>
                                                            <div className="table-responsive">
                                                                <table className="table table-sm table-hover border">
                                                                    <thead className="table-secondary">
                                                                        <tr style={{ fontSize: '0.85rem' }}>
                                                                            <th>{i18n.translate("branch_box")}</th>
                                                                            <th>{i18n.translate("range")}</th>
                                                                            <th>{i18n.translate("invoice_fiscal_stamp_number")}</th>
                                                                            <th>{i18n.translate("default_type")}</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {inv.details?.map((det, index) => (
                                                                            <tr key={`det-${det.fiscalStampNumber}-${det.startNumber}-${index}`} style={{ fontSize: '0.85rem' }}>
                                                                                <td>{det.invoiceBranch}-{det.invoicePrinter}</td>
                                                                                <td><span className="fw-bold">{det.startNumber}</span> al <span className="fw-bold">{det.endNumber}</span></td>
                                                                                <td>{det.fiscalStampNumber}</td>
                                                                                <td>{det.typeDocument}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>

                                                        {/* COLUMNA IZQUIERDA: RESUMEN Y NOTIFICACIONES */}
                                                        <div className="col-md-5 border-end">
                                                            <h6 className="text-primary mb-3">{i18n.translate("summary")}</h6>
                                                            <div className="mb-3">
                                                                <small className="text-muted">{i18n.translate("period")}:</small>
                                                                <h5 className="mb-0">{inv.period}</h5>
                                                                <span className={`badge ${inv.state === 'PENDIENTE' ? 'bg-warning text-dark' : 'bg-success'}`}>
                                                                    {inv.state}
                                                                </span>
                                                            </div>

                                                            <small className="text-muted d-block mb-2">{i18n.translate("history_of_notifications")} ({inv.notifications?.length}):</small>
                                                            {/* Contenedor con scroll para no alargar la tabla demasiado */}
                                                            <div className="border rounded p-2 bg-light" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                                <ul className="list-group list-group-flush">
                                                                    {inv.notifications?.map((notif, index) => (
                                                                        <li key={`notif-${notif.notificationAttempt}-${notif.userEmail}-${index}`} className="list-group-item bg-transparent py-1 px-1" style={{ fontSize: '0.85rem' }}>
                                                                            <span className="badge bg-secondary me-2">#{notif.notificationAttempt}</span>
                                                                            <strong>{notif.notificationDate ? new Date(notif.notificationDate).toLocaleDateString() : ""}</strong> - {notif.userEmail}
                                                                        </li>
                                                                    ))}
                                                                    {(!inv.notifications || inv.notifications.length === 0) && (
                                                                        <li className="text-muted small p-2">{i18n.translate("no_notifications_registered")}</li>
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        ) : (
                            <tr><td colSpan={5} className="text-center py-5">{i18n.translate("no_results_found")}</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {modalOpen && (
                <DefaultModal
                    show={true}
                    title="Motivo de Inutilización"
                    content={
                        <div>
                            <p>Por favor explique el motivo de la inutilización (mínimo 5 caracteres).</p>
                            <FormGroup>
                                <Label for="motiveText">Motivo:</Label>
                                <Input
                                    type="textarea"
                                    name="text"
                                    id="motiveText"
                                    rows="4"
                                    value={invalidationMotive}
                                    onChange={(e) => {
                                        setInvalidationMotive(e.target.value);
                                        if (motiveError) setMotiveError("");
                                    }}
                                />
                            </FormGroup>
                            {motiveError && <Alert color="danger">{motiveError}</Alert>}
                        </div>
                    }
                    buttons={[
                        { text: "Confirmar", function: handleConfirmInvalidate, class: "btn-primary" },
                        { text: "Cancelar", function: toggleModal, class: "btn-secondary" }
                    ]}
                    modalClass=""
                    closeFunction={toggleModal}
                    size="lg"
                />
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .status-badge-oval { padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: bold; width: 100px; text-align: center; margin: 0 auto; display: block; }
                .bg-status-pending { background-color: #ffeeba; color: #856404; }
                .bg-status-sent { background-color: #d4edda; color: #155724; }
                .bg-status-expired { background-color: #f8d7da; color: #721c24; }
                .table-outline { border: 1px solid #dee2e6; }
            `}} />
        </div>
    );
};

InvoiceNumberInvalidate.propTypes = {
    token: PropTypes.string.isRequired,
};

export default InvoiceNumberInvalidate;